/**
 * Local, visitor-safe shape for a topic thread in the messenger's conversation
 * list (multi-topic threads).
 *
 * DELIBERATELY decoupled from the GraphQL generated types for now: the backend
 * `VisitorThread` DTO is mid-implementation (Conversation Model Phase 3) and
 * its exact field names may still shift, and the dev server isn't codegen-able
 * against it yet. So the UI is built against THIS semantic type + mock data;
 * once the BE contract is live + introspection-verified, a thin adapter maps
 * the generated `VisitorThread` → `ThreadListItem` and a field-name change
 * touches only that adapter, not the UI.
 *
 * Only visitor-safe fields live here — never operator identity (`assignedTo`,
 * email), other merged visitors' `profile`/`people`, or `members`.
 */

/** Subset of the BE `ChatStatus` the widget cares about. Drives the small
 *  status hint on a thread row (e.g. a "resolved" affordance). */
export type ThreadStatus = "OPEN" | "PENDING" | "RESOLVED" | "CLOSED";

/**
 * Safe preview of a thread's last VISITOR-FACING message (never internal notes
 * / system rows). Modelled as `{ text, at, fromVisitor }` so the codegen
 * adapter absorbs whichever preview shape the BE ships — a flat
 * `lastMessagePreview`/`lastMessageAt`/`lastMessageFromVisitor` trio OR a
 * nested `VisitorChatHistoryItem` — without touching the UI. `null` when the
 * thread has no visitor-facing message yet.
 */
export interface ThreadPreview {
  text: string;
  /** ISO timestamp of the previewed message. */
  at: string;
  /** Whether the previewed message was the visitor's own. */
  fromVisitor: boolean;
  /** The previewed message is media-only (image / gif / sticker / audio / file)
   *  with no text — `text` is empty, and the row/peek renders a media label
   *  instead of a blank line. Set for BOTH the typed path (`mediaType` present)
   *  and the untyped fallback (empty preview from a real sender, BE didn't type
   *  it — e.g. an unsupported media kind → `mediaType` null). */
  media?: boolean;
  /** BE media discriminator (PHOTO/VIDEO/AUDIO/VOICE/FILE/GIF/STICKER) → drives
   *  the type icon; `null`/absent = untyped media or text. */
  mediaType?: string | null;
  /** Thumbnail URL for visual media (PHOTO/VIDEO/GIF/STICKER) — shown as a small
   *  preview image in the row/peek. */
  mediaThumbUrl?: string | null;
  /** Quick-reply chips on the previewed message, when it carried buttons —
   *  surfaced in the launcher peek so the visitor can answer without opening
   *  the panel. Absent for a plain-text last message. */
  buttons?: { text: string; payload: string }[];
}

/** Language-neutral type glyph for a media preview (used as an inline icon in
 *  the thread-list row + launcher peek; the localized word comes from i18n).
 *  Falls back to a paperclip for the untyped / unsupported case. */
export function mediaTypeIcon(mediaType: string | null | undefined): string {
  switch (mediaType) {
    case "PHOTO":
      return "🖼️";
    case "VIDEO":
      return "🎥";
    case "AUDIO":
      return "🎵";
    case "VOICE":
      return "🎤";
    case "FILE":
      return "📄";
    case "GIF":
      return "🎞️";
    case "STICKER":
      return "🩷";
    default:
      return "📎";
  }
}

export interface ThreadListItem {
  /** The thread's chat id (`VisitorThread.id`) — drives history + the
   *  single open-thread SSE subscription once wired to the live BE. */
  chatId: string;
  /** Per-topic key the widget mints for "new question" threads. `null` for
   *  the anchor / main thread, which is always sent WITHOUT a threadKey. */
  threadKey: string | null;
  /** The slot text of a `message:<slotId>` thread (BE `VisitorThread.title`, <= 120 chars): the row
   *  is titled with it. `null` / absent for a regular thread. */
  title?: string | null;
  status: ThreadStatus;
  /** Unread visitor-facing messages on this thread (`unReadCount`). */
  unreadCount: number;
  /** ISO timestamp of the last activity (`updatedAt`) — orders the list. */
  updatedAt: string;
  /** Last visitor-facing message preview; `null` for a thread with none. */
  preview: ThreadPreview | null;
  /** Display name for the row (the AI assistant or operator the visitor is
   *  talking to on this thread). */
  senderName: string;
  /** Who sent the LAST message on this thread (from `visitorThreads`, so it's
   *  known on initial load) — "visitor" | "operator" | "ai" | "bot" | "system",
   *  or `null` when the thread has no message. Lets the row show an operator's
   *  real name instead of the AI persona even when no assignment event arrived. */
  lastSenderKind?: string | null;
  /** The OPERATOR display name for the last message (present only when
   *  `lastSenderKind === "operator"`; `null` otherwise). */
  lastSenderName?: string | null;
  /** Id of the thread's last message (from `visitorThreads`; null on a thread with no message). A
   *  `lastMessageDeletedId` frame is matched against it before any content frame has arrived. */
  lastMessageId?: string | null;
  /** Optional avatar URL for the row. */
  senderAvatar?: string;
  /** Live transient — someone is currently typing in this thread (from the
   *  visitor-level thread-status stream). Not part of the persisted query. */
  typing?: boolean;
  /** Live transient — operator-handoff state for this thread: `"connected"`
   *  (an operator is on it), `"pending"` (assigned, awaiting accept), or
   *  `null`/undefined (AI-only). */
  assignedStatus?: "connected" | "pending" | null;
  /** Live transient — the connected operator's display name, when handed off. */
  assignedOperatorName?: string | null;
  /** This thread is blocked (`VisitorThread.blocked`) — the visitor cannot send
   *  into it. True when the thread itself is blocked OR the visitor is globally
   *  blocked; live-updated by a `blockScope: "THREAD"` stream event. */
  blocked?: boolean;
}

/** True while the thread has no real chat yet — a freshly-minted "new
 *  question" thread before its first message is sent (chatId arrives on the
 *  send ack). Such a thread isn't in `visitorThreads` and renders as the
 *  active empty conversation, not a list row. */
export function isPendingThread(item: Pick<ThreadListItem, "chatId">): boolean {
  return !item.chatId;
}
